import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, CreditCard, Armchair, TrendingUp, Download, LogOut,
  Search, Edit2, Trash2, CheckCircle, Clock, X, RefreshCw,
  Phone, Mail, Calendar, FileText, IndianRupee, AlertCircle,
  Bell, MessageCircle, ExternalLink, Image
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = ({ token, onLogout }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [payments, setPayments] = useState([]);
  const [seats, setSeats] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingStudent, setEditingStudent] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [viewingImage, setViewingImage] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, studentsRes, paymentsRes, seatsRes, remindersRes] = await Promise.all([
        axios.get(`${API}/api/dashboard/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/students`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/payments`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/seats`),
        axios.get(`${API}/api/reminders/pending`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setStats(statsRes.data);
      setStudents(studentsRes.data);
      setPayments(paymentsRes.data);
      setSeats(seatsRes.data);
      setReminders(remindersRes.data.pending_reminders || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateStudent = async (studentId, updates) => {
    try {
      await axios.put(`${API}/api/students/${studentId}`, updates, { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
      setEditingStudent(null);
    } catch (err) {
      alert('Failed to update student');
    }
  };

  const deleteStudent = async (studentId) => {
    try {
      await axios.delete(`${API}/api/students/${studentId}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
      setShowDeleteModal(null);
    } catch (err) {
      alert('Failed to delete student');
    }
  };

  const markReminderSent = async (studentId) => {
    try {
      await axios.post(`${API}/api/reminders/mark-sent/${studentId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
    } catch (err) {
      alert('Failed to mark reminder');
    }
  };

  const handleExport = async (type) => {
    try {
      const response = await axios.get(`${API}/api/export/${type}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mindspace_${type}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('Export failed');
    }
  };

  const filteredStudents = students.filter(s =>
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.phone?.includes(searchTerm) ||
    s.exam?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const StatCard = ({ icon: Icon, label, value, color, subValue, onClick }) => (
    <div 
      className={`bg-white rounded-2xl p-6 shadow-lg border-l-4 ${color} ${onClick ? 'cursor-pointer hover:shadow-xl transition' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-500 text-sm">{label}</p>
          <p className="text-3xl font-bold text-gray-800">{value}</p>
          {subValue && <p className="text-sm text-gray-400 mt-1">{subValue}</p>}
        </div>
        <div className={`p-4 rounded-full bg-opacity-20 ${color.replace('border-', 'bg-')}`}>
          <Icon className={`w-8 h-8 ${color.replace('border-', 'text-')}`} />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin text-purple-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold">M</span>
            </div>
            <div>
              <h1 className="font-bold text-gray-800">MINDSPACE Admin</h1>
              <p className="text-xs text-gray-500">Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={fetchData}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
              title="Refresh"
            >
              <RefreshCw className="w-5 h-5 text-gray-600" />
            </button>
            <button
              onClick={onLogout}
              className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex gap-1 overflow-x-auto">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: TrendingUp },
              { id: 'students', label: 'Students', icon: Users },
              { id: 'payments', label: 'Payments', icon: CreditCard },
              { id: 'seats', label: 'Seats', icon: Armchair },
              { id: 'reminders', label: 'Reminders', icon: Bell, badge: reminders.length }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 md:px-6 py-4 font-medium transition border-b-2 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.badge > 0 && (
                  <span className="ml-1 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && stats && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                icon={Users}
                label="Total Students"
                value={stats.total_students}
                color="border-blue-500"
              />
              <StatCard
                icon={Armchair}
                label="Available Seats"
                value={stats.available_seats}
                color="border-green-500"
                subValue={`${stats.occupied_seats} / ${stats.total_seats} occupied`}
              />
              <StatCard
                icon={Clock}
                label="Pending Payments"
                value={stats.pending_payments}
                color="border-yellow-500"
              />
              <StatCard
                icon={IndianRupee}
                label="Total Revenue"
                value={`₹${stats.total_revenue?.toLocaleString() || 0}`}
                color="border-purple-500"
                subValue={`${stats.paid_payments} paid`}
              />
            </div>

            {/* Expiring Soon Alert */}
            {stats.expiring_soon > 0 && (
              <div 
                className="bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-center gap-4 cursor-pointer hover:bg-orange-100 transition"
                onClick={() => setActiveTab('reminders')}
              >
                <div className="p-3 bg-orange-100 rounded-full">
                  <Bell className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="font-bold text-orange-800">
                    {stats.expiring_soon} plan(s) expiring within 7 days
                  </p>
                  <p className="text-sm text-orange-600">Click to view and send reminders</p>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h2 className="text-lg font-bold text-gray-800 mb-4">Quick Actions</h2>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => handleExport('students')}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition"
                >
                  <Download className="w-4 h-4" /> Export Students
                </button>
                <button
                  onClick={() => handleExport('payments')}
                  className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition"
                >
                  <Download className="w-4 h-4" /> Export Payments
                </button>
              </div>
            </div>

            {/* Recent Students */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h2 className="text-lg font-bold text-gray-800 mb-4">Recent Registrations</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-gray-500 text-sm border-b">
                      <th className="pb-3">Name</th>
                      <th className="pb-3">Phone</th>
                      <th className="pb-3">Plan</th>
                      <th className="pb-3">Seat</th>
                      <th className="pb-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.slice(0, 5).map(student => (
                      <tr key={student.id} className="border-b last:border-0">
                        <td className="py-3 font-medium">{student.name}</td>
                        <td className="py-3 text-gray-600">{student.phone}</td>
                        <td className="py-3 text-gray-600">{student.plan}</td>
                        <td className="py-3">
                          <span className="px-2 py-1 bg-purple-100 text-purple-600 rounded-lg text-sm">
                            #{student.seat}
                          </span>
                        </td>
                        <td className="py-3">
                          <span className={`px-2 py-1 rounded-lg text-sm ${
                            student.payment_status === 'Paid'
                              ? 'bg-green-100 text-green-600'
                              : 'bg-yellow-100 text-yellow-600'
                          }`}>
                            {student.payment_status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Students Tab */}
        {activeTab === 'students' && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4 justify-between">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search students..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:border-purple-500"
                />
              </div>
              <button
                onClick={() => handleExport('students')}
                className="flex items-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition"
              >
                <Download className="w-4 h-4" /> Export Excel
              </button>
            </div>

            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr className="text-left text-gray-600 text-sm">
                      <th className="px-4 py-4">ID</th>
                      <th className="px-4 py-4">Name</th>
                      <th className="px-4 py-4">Phone</th>
                      <th className="px-4 py-4">Plan</th>
                      <th className="px-4 py-4">End Date</th>
                      <th className="px-4 py-4">Seat</th>
                      <th className="px-4 py-4">Docs</th>
                      <th className="px-4 py-4">Payment</th>
                      <th className="px-4 py-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.map(student => (
                      <tr key={student.id} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-4 text-gray-500">#{student.student_id}</td>
                        <td className="px-4 py-4 font-medium">{student.name}</td>
                        <td className="px-4 py-4 text-gray-600">{student.phone}</td>
                        <td className="px-4 py-4 text-gray-600 text-sm">{student.plan}</td>
                        <td className="px-4 py-4 text-gray-600 text-sm">{student.plan_end_date || '-'}</td>
                        <td className="px-4 py-4">
                          <span className="px-2 py-1 bg-purple-100 text-purple-600 rounded text-sm">
                            {student.seat}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex gap-1">
                            {student.photo_file && (
                              <button
                                onClick={() => setViewingImage(`${API}/api/uploads/${student.photo_file}`)}
                                className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                title="View Photo"
                              >
                                <Image className="w-4 h-4" />
                              </button>
                            )}
                            {student.id_proof_file && (
                              <button
                                onClick={() => setViewingImage(`${API}/api/uploads/${student.id_proof_file}`)}
                                className="p-1 text-green-600 hover:bg-green-50 rounded"
                                title="View ID Proof"
                              >
                                <FileText className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <select
                            value={student.payment_status}
                            onChange={(e) => updateStudent(student.id, { payment_status: e.target.value })}
                            className={`px-2 py-1 rounded text-sm border-0 cursor-pointer ${
                              student.payment_status === 'Paid'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-yellow-100 text-yellow-700'
                            }`}
                          >
                            <option value="Pending">Pending</option>
                            <option value="Paid">Paid</option>
                          </select>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => setEditingStudent(student)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setShowDeleteModal(student)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredStudents.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    No students found
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <button
                onClick={() => handleExport('payments')}
                className="flex items-center gap-2 px-4 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition"
              >
                <Download className="w-4 h-4" /> Export Payments
              </button>
            </div>

            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr className="text-left text-gray-600 text-sm">
                      <th className="px-4 py-4">Student</th>
                      <th className="px-4 py-4">Phone</th>
                      <th className="px-4 py-4">Plan</th>
                      <th className="px-4 py-4">Amount</th>
                      <th className="px-4 py-4">Type</th>
                      <th className="px-4 py-4">Date</th>
                      <th className="px-4 py-4">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map(payment => (
                      <tr key={payment.id} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-4 font-medium">{payment.student_name}</td>
                        <td className="px-4 py-4 text-gray-600">{payment.phone}</td>
                        <td className="px-4 py-4 text-gray-600 text-sm">{payment.plan}</td>
                        <td className="px-4 py-4 font-bold text-green-600">
                          ₹{payment.amount?.toLocaleString()}
                        </td>
                        <td className="px-4 py-4">
                          <span className={`px-2 py-1 rounded text-sm ${
                            payment.payment_type === 'Online'
                              ? 'bg-blue-100 text-blue-600'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {payment.payment_type}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-gray-500 text-sm">
                          {new Date(payment.payment_date).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-4">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            payment.status === 'Paid'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {payment.status === 'Paid' ? (
                              <><CheckCircle className="w-3 h-3 inline mr-1" /> Paid</>
                            ) : (
                              <><Clock className="w-3 h-3 inline mr-1" /> Pending</>
                            )}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {payments.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    No payments found
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Seats Tab */}
        {activeTab === 'seats' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h2 className="text-lg font-bold text-gray-800 mb-2">Seat Map</h2>
              <p className="text-gray-500 mb-6">
                🟢 Available ({seats.filter(s => !s.is_occupied).length}) | 
                🔴 Occupied ({seats.filter(s => s.is_occupied).length})
              </p>
              
              <div className="grid grid-cols-6 sm:grid-cols-9 md:grid-cols-12 gap-3">
                {seats.map(seat => (
                  <div
                    key={seat.seat_number}
                    className={`relative p-3 rounded-xl text-center transition cursor-pointer group ${
                      seat.is_occupied
                        ? 'bg-red-100 border-2 border-red-200'
                        : 'bg-green-100 border-2 border-green-200'
                    }`}
                    title={seat.is_occupied ? `${seat.student_name}` : 'Available'}
                  >
                    <Armchair className={`w-6 h-6 mx-auto ${
                      seat.is_occupied ? 'text-red-500' : 'text-green-500'
                    }`} />
                    <span className="text-sm font-bold">{seat.seat_number}</span>
                    
                    {seat.is_occupied && (
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition whitespace-nowrap z-10">
                        {seat.student_name}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Occupied Seats List */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h2 className="text-lg font-bold text-gray-800 mb-4">Occupied Seats</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {seats.filter(s => s.is_occupied).map(seat => (
                  <div key={seat.seat_number} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <span className="font-bold text-purple-600">{seat.seat_number}</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">{seat.student_name}</p>
                      <p className="text-xs text-gray-500">Seat #{seat.seat_number}</p>
                    </div>
                  </div>
                ))}
              </div>
              {seats.filter(s => s.is_occupied).length === 0 && (
                <p className="text-gray-500 text-center py-8">No seats occupied yet</p>
              )}
            </div>
          </div>
        )}

        {/* Reminders Tab */}
        {activeTab === 'reminders' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-bold text-gray-800">Plan Expiry Reminders</h2>
                  <p className="text-gray-500 text-sm">Students whose plans are expiring within 7 days</p>
                </div>
              </div>

              {reminders.length === 0 ? (
                <div className="text-center py-12">
                  <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No plans expiring soon</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {reminders.map(reminder => (
                    <div 
                      key={reminder.id} 
                      className={`p-4 rounded-xl border-2 ${
                        reminder.days_left <= 3 
                          ? 'border-red-200 bg-red-50' 
                          : 'border-yellow-200 bg-yellow-50'
                      }`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            reminder.days_left <= 3 ? 'bg-red-100' : 'bg-yellow-100'
                          }`}>
                            <span className={`text-xl font-bold ${
                              reminder.days_left <= 3 ? 'text-red-600' : 'text-yellow-600'
                            }`}>
                              {reminder.days_left}
                            </span>
                          </div>
                          <div>
                            <p className="font-bold text-gray-800">{reminder.name}</p>
                            <p className="text-sm text-gray-500">{reminder.phone}</p>
                            <p className="text-sm text-gray-600">
                              {reminder.plan} • Ends: {reminder.end_date}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {reminder.reminder_sent ? (
                            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                              <CheckCircle className="w-4 h-4 inline mr-1" /> Sent
                            </span>
                          ) : (
                            <>
                              <a
                                href={reminder.whatsapp_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
                              >
                                <MessageCircle className="w-4 h-4" />
                                WhatsApp
                                <ExternalLink className="w-3 h-3" />
                              </a>
                              <button
                                onClick={() => markReminderSent(reminder.id)}
                                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                              >
                                Mark Sent
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <h3 className="font-bold text-blue-800 mb-2">⚡ Automatic SMS Reminders</h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>✅ <strong>AUTO-ENABLED</strong> - SMS sent automatically 3 days before plan expires</li>
                <li>⏰ Runs daily at <strong>9:00 AM IST</strong></li>
                <li>📱 Students receive: "Hi [Name]! Your MINDSPACE plan expires on [Date]. Renew now..."</li>
                <li>📋 You can also manually send WhatsApp reminders using buttons above</li>
              </ul>
            </div>
          </div>
        )}
      </main>

      {/* Edit Student Modal */}
      {editingStudent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Edit Student</h2>
              <button onClick={() => setEditingStudent(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={editingStudent.name}
                  onChange={(e) => setEditingStudent({ ...editingStudent, name: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="text"
                  value={editingStudent.phone}
                  onChange={(e) => setEditingStudent({ ...editingStudent, phone: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
                <select
                  value={editingStudent.payment_status}
                  onChange={(e) => setEditingStudent({ ...editingStudent, payment_status: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-purple-500"
                >
                  <option value="Pending">Pending</option>
                  <option value="Paid">Paid</option>
                </select>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setEditingStudent(null)}
                  className="flex-1 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => updateStudent(editingStudent.id, {
                    name: editingStudent.name,
                    phone: editingStudent.phone,
                    payment_status: editingStudent.payment_status
                  })}
                  className="flex-1 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-bold mb-2">Delete Student?</h2>
              <p className="text-gray-500 mb-6">
                Are you sure you want to delete <strong>{showDeleteModal.name}</strong>? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteModal(null)}
                  className="flex-1 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteStudent(showDeleteModal.id)}
                  className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Viewer Modal */}
      {viewingImage && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setViewingImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <button 
              onClick={() => setViewingImage(null)}
              className="absolute -top-10 right-0 p-2 text-white hover:text-gray-300"
            >
              <X className="w-8 h-8" />
            </button>
            <img 
              src={viewingImage} 
              alt="Document" 
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
